import {
  RenderElementProps,
  TableCellElement,
  TableElement,
  TableRowElement,
  CustomElement,
} from "components/ManualBuilder/types";
import { ElementRenderer } from "../elementMap";
import React, { useState, useRef, useEffect } from "react";
import { Editor, Element as SlateElement, Transforms, Path } from "slate";
import { useSlate } from "slate-react";
import { TableEditor } from "slate-table";
import { useTranslation } from "react-i18next";
import {
  ColumnInsertLeft,
  ColumnInsertRight,
  ColumnRemove,
  RowInsertBottom,
  RowInsertTop,
  RowRemove,
} from "./icons";

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  editor: Editor;
}

const TableContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  onClose,
  editor,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const insertRowBelow = () => {
    TableEditor.insertRow(editor);
    onClose();
  };

  const insertRowAbove = () => {
    // Insert row above current position
    TableEditor.insertRow(editor, { before: true });
    onClose();
  };

  const removeRow = () => {
    TableEditor.removeRow(editor);
    onClose();
  };

  const insertColumnRight = () => {
    TableEditor.insertColumn(editor);
    onClose();
  };

  const insertColumnLeft = () => {
    // Insert column to the left of current position
    TableEditor.insertColumn(editor, { before: true });
    onClose();
  };

  const removeColumn = () => {
    TableEditor.removeColumn(editor);
    onClose();
  };

  const deleteTable = () => {
    TableEditor.removeTable(editor);
    onClose();
  };

  return (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: y,
        left: x,
        zIndex: 1000,
      }}
      className="bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 w-48"
    >
      <div className="py-1" role="menu">
        <button
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
          onClick={insertRowAbove}
        >
          {t("manual.table.actions.add_row_above")}
          <div className="flex-shrink-0 ml-2">
            <RowInsertTop height={20} width={20} />
          </div>
        </button>
        <button
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
          onClick={insertRowBelow}
        >
          {t("manual.table.actions.add_row_below")}
          <div className="flex-shrink-0 ml-2">
            <RowInsertBottom height={20} width={20} />
          </div>
        </button>
        <button
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
          onClick={removeRow}
        >
          {t("manual.table.actions.remove_row")}
          <div className="flex-shrink-0 ml-2">
            <RowRemove height={20} width={20} />
          </div>
        </button>
        <div className="border-t border-gray-200 my-1"></div>
        <button
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
          onClick={insertColumnLeft}
        >
          {t("manual.table.actions.add_column_left")}
          <div className="flex-shrink-0 ml-2">
            <ColumnInsertLeft height={20} width={20} />
          </div>
        </button>
        <button
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
          onClick={insertColumnRight}
        >
          {t("manual.table.actions.add_column_right")}
          <div className="flex-shrink-0 ml-2">
            <ColumnInsertRight height={20} width={20} />
          </div>
        </button>
        <button
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
          onClick={removeColumn}
        >
          {t("manual.table.actions.remove_column")}
          <div className="flex-shrink-0 ml-2">
            <ColumnRemove height={20} width={20} />
          </div>
        </button>
        <div className="border-t border-gray-200 my-1"></div>
        <button
          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center justify-between"
          onClick={deleteTable}
        >
          {t("manual.table.actions.delete_table")}
          <div className="flex-shrink-0 ml-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
};

export const tableElement: ElementRenderer =
  () =>
  ({ attributes, children }: RenderElementProps) => {
    const [contextMenu, setContextMenu] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const editor = useSlate();

    const handleContextMenu = (event: React.MouseEvent) => {
      event.preventDefault();
      setContextMenu({ x: event.clientX, y: event.clientY });
    };

    return (
      <div className="my-4 overflow-x-auto">
        <table
          {...attributes}
          className="w-full border-collapse"
          onContextMenu={handleContextMenu}
        >
          <tbody className="bg-white">{children}</tbody>
        </table>
        {contextMenu && (
          <TableContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            editor={editor}
          />
        )}
      </div>
    );
  };

export const tableRowElement: ElementRenderer =
  () =>
  ({ attributes, children }: RenderElementProps) => {
    return (
      <tr {...attributes} className="border-b border-gray-300">
        {children}
      </tr>
    );
  };

export const tableCellElement: ElementRenderer =
  () =>
  ({ attributes, children, element }: RenderElementProps) => {
    const cellElement = element as TableCellElement;

    const cellStyle = {
      position: "relative",
      minWidth: "120px",
    } as const;

    const overlayStyle = {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      pointerEvents: "none",
      backgroundColor: "transparent",
      border: "2px solid transparent",
    } as const;

    return cellElement.isHeader ? (
      <th
        {...attributes}
        className="border border-gray-300 bg-gray-100 p-2 text-left font-semibold"
        style={cellStyle}
      >
        <div contentEditable={false} style={overlayStyle} />
        {children}
      </th>
    ) : (
      <td
        {...attributes}
        className="border border-gray-300 p-2"
        style={cellStyle}
      >
        <div contentEditable={false} style={overlayStyle} />
        {children}
      </td>
    );
  };
